/*
 * "About this report" content, keyed by route. Shown by ReportGuideButton in
 * the site header on the report page itself, and by ReportGuideIcon on the
 * report index cards.
 *
 * Shape per report:
 *   title      — as the report is named in the menu
 *   purpose    — the question it answers, in one or two sentences
 *   whenToUse  — the moment someone should open it
 *   howToUse   — numbered steps through the controls actually on the page
 *   whatYouGet — what the page shows
 *   terms      — [term, meaning] for every number that is not self-evident
 *   tips       — traps and defaults that make people misread the page
 *
 * Keep this in step with the report when its filters or columns change.
 */

const SALES = {
    "/reports/all": {
        title: "All-in-One Report",
        purpose: "Every sales report on one page, under one set of filters, so you can review the whole sales operation in a single sitting and send it as one Excel file.",
        whenToUse: "Weekly or monthly sales reviews, management meetings, or when you need one file that has everything.",
        howToUse: [
            "Pick the period at the top (it starts on This Month). For Custom Range, set both dates.",
            "Optionally narrow to one rep, choose the forecast scenario (Most Likely / Best Case / Worst Case) and the AMC window.",
            "Read the headline numbers first, then click a section name to jump to it.",
            "Use Collapse/Expand all to tidy the page, and 'Full report' on any section to open its standalone page.",
            "Use Download Excel for the complete data, or Print / PDF to share the page as it looks.",
        ],
        whatYouGet: [
            "Headline numbers: Total Leads, Open Pipeline, Weighted Forecast, Closed This Month, Conversion Rate, Deals Won, Hot Leads, Stale Deals, Email Open Rate, AMC Overdue, Onboarded Revenue, Delayed Milestones.",
            "12 collapsible sections, each with summary chips, breakdown bars and a table.",
            "An Excel file with an Executive Summary sheet plus one sheet per report, with every row.",
        ],
        terms: [
            ["Weighted Forecast", "Pipeline value adjusted by the chance of closing, using the forecast scenario you picked."],
            ["Stale Deals", "Deals older than 30 days that were never followed up, or not followed up in the last 14 days."],
        ],
        tips: [
            "Each section shows only the first 25 rows on screen — the Excel file has all of them.",
            "Sections you do not have permission for are hidden and counted as 'hidden (no access)'.",
        ],
    },

    "/reports/timeline": {
        title: "Timeline Report",
        purpose: "A log of every stage change: which lead moved, from which stage to which, who moved it and when, with the remark entered at the time.",
        whenToUse: "To check what the team actually did on a given day, or to see how a particular lead progressed.",
        howToUse: [
            "Choose a date range — it opens on Today, so widen it if the page looks empty.",
            "Filter by rep, or click a stage chip to see only moves into that stage.",
            "Search by customer name, mobile number or rep.",
            "Click any row to open that customer.",
        ],
        whatYouGet: [
            "One row per stage change: Date, Customer, Mobile No, Rep, Transition (from → to), Remarks, Next Follow-up, Status.",
            "Stage chips with how many moves went into each stage in the period.",
        ],
        terms: [
            ["Stage chip count", "Number of moves into that stage, not the number of different leads — one lead moved twice counts twice."],
        ],
        tips: [
            "The same lead can appear several times if it moved more than once.",
            "Your filters are remembered for next time. There is no Excel export on this page.",
        ],
    },

    "/reports/ai-suggestions": {
        title: "AI Next Actions",
        purpose: "An AI-prepared to-do list: for each open lead, the next best action (call, reschedule demo, send proposal, escalate…) with the reason and a suggested opening line.",
        whenToUse: "At the start of the day, to decide which leads to work first.",
        howToUse: [
            "Read the Manager Briefing at the top for the overall picture.",
            "Stay on the Pending tab and work from the highest priority down. Filter by rep or by action type with the chips.",
            "Open 'Show suggested opener' for a ready script, and Copy it.",
            "Click Accept when you will act on it, Dismiss if it is wrong, and Mark done once finished.",
        ],
        whatYouGet: [
            "One card per lead: customer, stage, phone, rep, recommended action, priority and the reasoning behind it.",
            "A dated Manager Briefing: how many leads were analysed and how many are urgent.",
        ],
        terms: [
            ["Priority", "0–100. 80 and above is Urgent (red), 50–79 is This week (amber), below 50 is Low."],
        ],
        tips: [
            "Suggestions are generated in a batch, not live — they reflect the leads as they were when last generated.",
            "A card leaves the current tab as soon as you change its status; find it again under Accepted, Done or All.",
            "If the page is empty, suggestions have not been generated yet — ask your administrator.",
        ],
    },

    "/reports/team-performance": {
        title: "Team Performance",
        purpose: "Compares every rep side by side on activity, demos, wins, revenue and monthly targets.",
        whenToUse: "One-to-ones, monthly reviews, or to see who needs help and who is ahead of target.",
        howToUse: [
            "Choose the period (This Month by default). Tick 'Include past reps' to see people who have left.",
            "Click any column heading to sort — e.g. by Conv % or Overdue.",
            "Managers can use 'Set target' / 'Edit' to enter each rep's monthly demo, closure and revenue targets.",
            "Use Excel to download the table.",
        ],
        whatYouGet: [
            "Team totals: Reps, Open Pipeline, Activities, Demos, Won, Revenue.",
            "Per rep: Open, Activities, Leads Worked, Demos Fixed, Demos Done, Show %, Won, Lost, Conv %, Revenue, Overdue, and progress bars against each target.",
        ],
        terms: [
            ["Open", "Open leads assigned to the rep right now — not affected by the date filter."],
            ["Activities", "Stage changes and notes logged in the period."],
            ["Leads Worked", "Different leads the rep touched at least once in the period."],
            ["Show %", "Demos done ÷ demos fixed. Green ≥ 50%, amber 30–49%, red below 30%."],
            ["Conv %", "Won ÷ leads worked. Green ≥ 5%, amber 2–4%, red below 2%."],
            ["Overdue", "Open leads whose promised follow-up date has passed."],
            ["Target bars", "Green when the target is met, amber at 60% or more, red below 60%."],
        ],
        tips: [
            "Show % can go above 100% when demos fixed before the period were held inside it.",
            "Targets are monthly, so they may not line up with a custom date range.",
        ],
    },

    "/reports/lead-assignment": {
        title: "Lead Assignment",
        purpose: "Shows whether new leads get an owner quickly, whether they are shared evenly across reps, and why any were left waiting.",
        whenToUse: "Weekly, and after changing teams, limits or rules in Settings → Lead Assignment.",
        howToUse: [
            "Pick a date range (last 30 days by default) and, if you like, one team.",
            "Read the tiles first: a low Automatic % or a long time to an owner means leads are waiting for someone to pick them up.",
            "Check 'Why leads were left unassigned' — 'Everyone is at their limit' means limits are too tight or the team is too small.",
            "Use Settings → Lead Assignment → History to see the reason behind any single lead.",
        ],
        whatYouGet: [
            "Tiles: leads given an owner, % automatic, unassigned right now, median time to an owner, fairness.",
            "Automatic assignments per day, leads per rep, unassigned reasons, how owners were set, rule performance and new leads by source.",
        ],
        terms: [
            ["Automatic", "Given an owner by the assignment engine: by a rule, the default team, a returning owner or the fallback."],
            ["Time to an owner", "From the lead arriving to the engine giving it an owner. Automatic assignments only."],
            ["Fairness", "Leads of the busiest rep ÷ leads of the quietest rep in the period. 1.0 is perfectly even; above 1.5 is worth a look (leave and limits explain some of it)."],
            ["Fell back", "A rule matched, but nobody in its team was free, so its fallback decided."],
            ["Before tracking", "Owner changes made before the assignment engine kept reasons."],
        ],
        tips: [
            "This report measures assignment, not sales. Conversion depends on the lead and the rep as much as on who got it.",
            "No export on this page — the History tab in Settings → Lead Assignment lists every owner change.",
        ],
    },

    "/reports/action-center": {
        title: "Action Center",
        purpose: "One page of everything that needs a decision today, worst first: missed demos, demos at risk, urgent leads, leads with no owner and follow-up backlogs.",
        whenToUse: "First thing every morning — especially for managers.",
        howToUse: [
            "Start with the cards: Needs action, Critical, Value exposed, Overdue follow-ups.",
            "Work through the sections top-down; Critical sections come first.",
            "Click a row to open the lead, or 'Work this list' to go to the page that handles that list (Demo Desk, AI Next Actions, Customers, Team Performance).",
        ],
        whatYouGet: [
            "A last-7-days strip: activities, demos held vs fixed, deals won.",
            "Five lists: Demos missed, Demos at risk, Urgent next actions, Leads with no active owner, Follow-up backlog.",
        ],
        terms: [
            ["Demos at risk", "Upcoming demos with a risk score of 70 or more."],
            ["Urgent next actions", "AI suggestions still pending with priority 80 or more."],
            ["No active owner", "Open leads with no rep, or whose rep is inactive."],
            ["Follow-up backlog", "Reps with 25 or more leads past their follow-up date."],
            ["Value exposed", "Total quoted value of the missed-demo and urgent leads."],
        ],
        tips: [
            "There are no date or rep filters — it is always 'now'.",
            "Reps see only their own items; the 'no owner' list is for managers.",
            "Each list shows up to 25 items ('showing X of Y').",
            "Organisations with Sales Manager Intelligence open on a 'Tracked alerts' tab: every problem the alert rules found, ranked by a priority score, with evidence, a recommended action and a status (new, acknowledged, in progress, resolved, dismissed, reopened). The lists above are on the 'Live checks' tab.",
            "A tracked alert resolves itself when its condition clears (for example a follow-up is booked) and comes back if a 'resolved' problem is still there on the next run.",
        ],
    },

    "/sales-agent": {
        title: "Sales Manager Intelligence",
        purpose: "What is happening in your sales team, what needs attention, and what to do about it, on one page.",
        whenToUse: "Every morning, and before a team meeting or 1:1.",
        howToUse: [
            "Read the daily briefing first: yesterday, today's priorities, risks and recommended actions. Each linked #number opens that alert in the Action Center.",
            "Pick the period (today, yesterday, last 7 days, this month) for the KPI cards and the team table. Hover a card to see exactly how it is calculated.",
            "Switch between the whole organisation, a sales team, or one person with the picker at the top. Click a name in the team table to look at that person.",
            "Work the 'Needs attention' list or open the Action Center for all alerts.",
        ],
        whatYouGet: [
            "A daily briefing, rule-based or AI-written; every number in an AI briefing has been checked against the CRM figures.",
            "Lead, activity and outcome KPIs, month target progress against pace, pipeline by stage, a team table and CRM data-quality counts.",
        ],
        terms: [
            ["—", "Not available: nothing to divide by, or the data does not exist (for example no stage probabilities set). Never shown as 0."],
            ["Contacted", "A person logged a call, message, visit, stage change or follow-up on the lead. System notes do not count."],
            ["Revenue won", "Final quoted price of the leads moved to Closed Won in the period, once per lead."],
            ["Weighted pipeline", "Open lead value x the stage probability in Settings -> Forecast Config. An estimate, not expected revenue."],
            ["Activity credit", "Calls, demos and wins count for whoever logged them; open leads and overdue follow-ups for the current owner."],
        ],
        tips: [
            "Times are IST. Figures are live; alert rules run every 30 minutes.",
            "The AI narrative is written by the AI worker PC (tick 'Sales manager briefings' there). Without it you still get the rule-based briefing.",
            "Compare people fairly: lead volume, sources and deal sizes differ.",
        ],
    },

    "/reports/demo-desk": {
        title: "Demo Desk",
        purpose: "Finds booked demos that are likely to fall through and drafts a confirmation or rescue message for each one.",
        whenToUse: "Daily, a day or two before demos, and right after a demo date passes without the stage moving.",
        howToUse: [
            "Stay on 'To action'. Demos are grouped into Needs rescue, Coming up and No date recorded.",
            "Read the risk reasons, then edit the drafted message if needed.",
            "Click 'Copy & open WhatsApp' — WhatsApp opens with the message filled in; you press send.",
            "Click Mark sent (or Skip) so the demo leaves your list.",
        ],
        whatYouGet: [
            "Cards: On the desk, High risk, Needs rescue, No date.",
            "Per demo: customer, date/time, phone, rep, risk score with up to 3 reasons, and a ready message.",
        ],
        terms: [
            ["Risk", "70+ is High risk, 45–69 is Watch, below 45 is Low risk."],
            ["Needs rescue", "The demo date has passed but the lead's stage never moved."],
            ["Inferred date", "No demo date was entered, so the follow-up date was used instead."],
        ],
        tips: [
            "Nothing is sent automatically — 'Mark sent' only records that you sent it.",
            "The list is prepared by a scheduled job; if it is empty, ask your administrator.",
        ],
    },

    "/reports/coaching": {
        title: "Coaching",
        purpose: "Shows what the best-performing reps do differently and gives each rep specific, AI-written coaching notes.",
        whenToUse: "Coaching sessions and one-to-ones.",
        howToUse: [
            "Read the Team briefing — it names the top performers everyone is compared against.",
            "Reps are listed weakest win rate first. Expand a rep to read their notes.",
            "Tick 'Show the numbers' to see the measurements behind the advice.",
        ],
        whatYouGet: [
            "Per rep: won, lost, win rate and coaching notes.",
            "With numbers on: Disqualified, Touches per win/loss, Days to close, Days to first contact, Demo show rate, Demos booked, Avg note length, Overdue follow-ups.",
        ],
        terms: [
            ["Touches per win/loss", "Average number of logged activities on leads that were won / lost."],
            ["Days to first contact", "How quickly a new lead was first worked."],
        ],
        tips: [
            "This is a snapshot from a scheduled job, not live. 'Demos booked' counts all time.",
        ],
    },

    "/reports/pipeline-activity": {
        title: "Pipeline Activity",
        purpose: "Deals in Proposal and Negotiation — what they are worth, how old they are, and whether anyone is following them up.",
        whenToUse: "Pipeline reviews and forecasting calls; to catch deals going cold.",
        howToUse: [
            "Set the date range (this filters by when the lead was created) and optionally a rep.",
            "Use the Proposal / Negotiation chips and search to narrow down.",
            "Look at amber rows and red 'Days Since' first — those deals are going stale.",
            "Click a row to open the lead; Export Excel downloads exactly what you filtered.",
        ],
        whatYouGet: [
            "Summary: total Pipeline ₹, average age, number of stale deals.",
            "Per deal: Created, Age, Customer, Stage, Pipeline Value, Expected Close, Lead Source, Lead Potential, Assigned To, Followups, Last Followup, Days Since, Next Followup, Latest Remarks.",
        ],
        terms: [
            ["Pipeline Value", "The latest quoted price on the lead."],
            ["Age", "Days since the lead was created."],
            ["Stale", "Older than 30 days and never followed up or last followed up more than 14 days ago (amber rows)."],
            ["Days Since", "Days since the last follow-up; red above 14."],
        ],
        tips: ["Only Proposal and Negotiation stages are included."],
    },

    "/reports/lead-sources": {
        title: "Lead Meta Form",
        purpose: "Lists new leads by the Meta lead form they came through, with city, stage and owner.",
        whenToUse: "To see which forms and cities are bringing leads in, and who they were assigned to.",
        howToUse: [
            "Set the date range — it opens on Today, so widen it for a fuller picture.",
            "Filter by lead form and rep, or click a stage chip.",
            "Click a row to open the lead.",
        ],
        whatYouGet: ["One row per lead: Created, Customer, Mobile No, City, Lead Form, Stage, Assigned To."],
        tips: [
            "The date is the lead's creation date.",
            "For lead quality per form (quality / disqualified / unanswered %), use Meta Performance instead.",
        ],
    },

    "/reports/meta-performance": {
        title: "Meta Performance",
        purpose: "Rates each Meta lead form on lead quality: what share of its leads became quality leads, were disqualified, never answered, or are still open.",
        whenToUse: "Before changing ad budgets or forms — to know which forms bring real buyers.",
        howToUse: [
            "Pick the period (Last 30 Days by default) and optionally a form or rep.",
            "Compare forms in the 'Performance by Lead Form' table — click a form to filter the lead list to it.",
            "Use the result chips (Quality / Disqualified / Unanswered / Open) to read the actual leads and their remarks.",
            "Export Excel downloads what you have filtered.",
        ],
        whatYouGet: [
            "Cards: Total Leads, Quality, Disqualified, Unanswered (count and %) and Open.",
            "Per form: Leads, Quality, Disqualified, Unanswered, Open and a mix bar.",
            "Lead list with stage, result, age and latest remarks.",
        ],
        terms: [
            ["Quality", "Lead is now in Qualified, Demo, Proposal, Negotiation or Won."],
            ["Disqualified", "Lead is now in Dis-Qualified or Closed Lost."],
            ["Unanswered", "Lead is now in Unanswered or No Show."],
            ["Open", "Lead is still New Lead, Contacted or On Hold."],
            ["%", "That result's count ÷ total leads."],
        ],
        tips: [
            "Results follow the lead's current stage, so last month's numbers can change as leads progress.",
            "Cards and the form table always cover all filtered leads, even while a result chip is selected.",
        ],
    },

    "/reports/lead-types": {
        title: "Lead Source",
        purpose: "Splits leads by lead source and shows whether each one has a follow-up planned.",
        whenToUse: "To see where leads come from and to find leads with an overdue or missing follow-up.",
        howToUse: [
            "Set the date range — it opens on Today.",
            "Click a source chip (sorted by count; 'Not Specified' means blank) and/or pick a rep.",
            "Scan the Status column for Overdue and No Follow-up.",
            "Click a row to open the customer.",
        ],
        whatYouGet: ["Per lead: Created, Customer, Mobile No, Type, Lead Source, Stage, Assigned To, Follow-up date, Status."],
        terms: [["Status", "Overdue (follow-up date passed), Today, Upcoming, or No Follow-up."]],
        tips: ["Chip colours follow rank, so a source's colour can change between visits. There is no export."],
    },
};

const SALES_MORE = {
    "/reports/lead-stages": {
        title: "Lead Stages",
        purpose: "Where every lead sits in the funnel right now, and whether it came from a Meta ad, the website or was entered manually.",
        whenToUse: "To see how many leads are working, stalled, won or lost, and which channel feeds each stage.",
        howToUse: [
            "Set the Created date range and optionally a rep or a name/phone search.",
            "Use the Origin toggle (All / Meta ads / Website / Manual) to compare channels.",
            "Click a phase tile (Working, Stalled, Won, Lost) or select stage rows to filter the lead list.",
            "Click a lead to open it. Remove filters from the 'Filtering by' strip.",
        ],
        whatYouGet: [
            "Phase tiles with count and %.",
            "Stage table: From Meta, Website, Manual, Total and share of all leads.",
            "Lead list with origin, stage, ad/form and owner.",
        ],
        terms: [
            ["Working", "New Lead, Contacted, Qualified, Demo Fixed, Demo Done, Proposal, Negotiation."],
            ["Stalled", "Unanswered, No Show, On Hold."],
            ["Won / Lost", "Won = Closed Won, Onboarded. Lost = Closed Lost, Dis-Qualified."],
            ["Origin", "Worked out from evidence: a Meta ad id or Meta form → Meta; a form named 'Website…' → Website; anything else → Manual. It can differ from the Lead Source typed on the lead."],
        ],
        tips: ["Empty stages are hidden — use 'Show N empty stages'. There is no export."],
    },

    "/reports/channel-partners": {
        title: "Channel Partner Sourcing",
        purpose: "Which channel partners send you leads, how many of those became business, and which partners have gone quiet.",
        whenToUse: "Partner reviews and when deciding which partners to invest time in.",
        howToUse: [
            "Pick the period and choose what to rank by (Leads Sent, Won, Conversion %, Won Value, Open Value).",
            "Turn on 'Sending only' to hide partners who sent nothing in the period.",
            "Click a partner to open their record; Excel downloads the table.",
        ],
        whatYouGet: [
            "Tiles: Partners, Leads Sent, Won, Conversion, Won Value, Open Value.",
            "Per partner: stage, leads, won, open, lost, conversion, won and open value, date of last lead.",
        ],
        terms: [
            ["Won / Lost / Open", "Won = Closed Won or Onboarded; Lost = Closed Lost or Dis-Qualified; Open = everything else."],
            ["Conversion", "Won ÷ (Won + Lost). Open leads do not count against the partner; '—' means nothing decided yet."],
            ["Value", "The lead's latest final quoted price."],
        ],
        tips: ["Greyed-out partners with zeros sent no leads in the period — that is deliberate, to show who has gone quiet."],
    },

    "/reports/lead-potential": {
        title: "Lead Potential",
        purpose: "Splits leads into Hot, Medium and Cold by the Lead Potential set on each lead.",
        whenToUse: "To focus calling on Hot leads, or to find leads nobody has rated yet.",
        howToUse: [
            "Set the date range (opens on Today — widen it) and press Apply for a custom range.",
            "Click Hot / Medium / Cold / Not Set, optionally pick a rep, and search.",
            "Click a lead to open it and update its potential.",
        ],
        whatYouGet: ["Per lead: Created, Customer, Mobile No, Type, Potential, Stage, City, State, Assigned To."],
        terms: [["Not Set", "The lead's potential field is blank."]],
        tips: ["Potential is entered by people, not calculated — for a calculated score use the Engagement report. No export."],
    },

    "/reports/sales-forecast": {
        title: "Sales Forecast",
        purpose: "How much revenue is likely to close, deal by deal, under worst-case, most-likely and best-case assumptions.",
        whenToUse: "Monthly forecasting and target-setting; committing expected deals for a month.",
        howToUse: [
            "Choose the Scenario, then the created date, rep and expected close month/year.",
            "Press Apply — date, rep and close filters do nothing until you do.",
            "Narrow with stage chips or search; the footer totals follow them.",
            "Use 'Commit' in the Projection column to lock a deal's expected value and month; 'Reschedule' to move it.",
        ],
        whatYouGet: [
            "Summary: Pipeline, Weighted, Open Deals.",
            "Per deal: Pipeline Value, Probability, Weighted Value, Expected Close, owner, potential and projection — highest weighted first.",
        ],
        terms: [
            ["Pipeline Value", "Latest final quoted price on the lead."],
            ["Probability", "Chance of closing for the lead's stage, set in Forecast settings. Best Case adds 15 points, Worst Case subtracts 20 (configurable)."],
            ["Weighted Value", "Pipeline Value × Probability."],
        ],
        tips: [
            "Only open stages are included (New Lead to Negotiation, plus On Hold, Unanswered, No Show).",
            "Choosing a close month/year drops deals with no expected close date.",
            "The summary at the top ignores stage chips and search; the footer totals respect them.",
        ],
    },

    "/reports/engagement": {
        title: "Engagement",
        purpose: "Scores every lead on how actively it has been worked in the last 30 days, and grades it Hot, Warm or Cold.",
        whenToUse: "To find leads that have gone cold before they are lost.",
        howToUse: [
            "Pick which leads to include by created date and rep.",
            "Click Cold to see neglected leads; sort your attention by Days Inactive.",
            "Click a lead to open it and log the next activity.",
        ],
        whatYouGet: ["Per lead: Score (0–100), Grade, Activities in the last 30 days, Last Activity and Days Inactive."],
        terms: [
            ["Score", "Points for activity in the last 30 days — stage change 15, call 10, email 8, note 6, WhatsApp 6, document 5 — minus 10 for every full week since the last activity. 0–100."],
            ["Grade", "Hot 71+, Warm 31–70, Cold 0–30."],
            ["Days Inactive", "Green under 7 days, amber 7–13, red 14+. 'Never' means nothing has been logged."],
        ],
        tips: ["The date filter chooses leads by creation date; the score always looks at the last 30 days."],
    },

    "/reports/lead-welcome": {
        title: "Lead Welcome & Engagement",
        purpose: "Follows every new lead from the automatic welcome (email and WhatsApp) through opening, replying and confirming their details, to the first call and the demo.",
        whenToUse: "Every morning for the 'Engaged, not called yet' list; weekly to see which source and which rep turns new leads into conversations.",
        howToUse: [
            "Pick a date range (last 30 days by default), and if you like one source or one rep.",
            "Call the leads in 'Engaged, not called yet' first — they have just shown interest.",
            "Read 'The journey' left to right to see where leads drop: not welcomed, welcomed but silent, or engaged but never called.",
            "Check 'Why a welcome was not sent' — 'No mobile number' or 'No email address' means the lead form is not collecting it.",
        ],
        whatYouGet: [
            "Tiles: new leads, % welcomed, % engaged, % confirmed, median time to first call, and engaged leads nobody has called.",
            "The journey step by step, leads per day, results by source and by rep, and the reasons welcomes were held back.",
        ],
        terms: [
            ["Welcomed", "The welcome email or WhatsApp was sent (Settings → Email Templates switches both on)."],
            ["Engaged", "Opened or clicked the email, read or replied on WhatsApp, or opened the confirm-details page."],
            ["Confirmed details", "Submitted the confirm-details page."],
            ["Verified", "Proved an email or mobile: submitted the page from the emailed link, or opened the WhatsApp link (only that number received it)."],
            ["First call", "The first call a rep logged on the lead, or the first outbound call from the CRM's telephony, whichever came first."],
            ["Source", "Meta when Meta's ad ids or lead form say so, Website for the website's forms, Manual for leads typed in."],
        ],
        tips: [
            "Leads are chosen by the date they arrived; their later steps count whenever they happened.",
            "WhatsApp 'read' depends on the lead's read receipts; a reply always counts as read.",
        ],
    },

    "/reports/demo-report": {
        title: "Demo Report",
        purpose: "Shows every booked demo: the reminders sent before it, whether the lead confirmed or moved it from the reminder link, and whether they turned up. A second tab has the feedback leads gave after the demo.",
        whenToUse: "Daily, for demos still marked 'Not updated'. Weekly, to see whether confirmed leads turn up more often than the rest.",
        howToUse: [
            "Pick a period. By default it covers the last 30 days and the next 7, so upcoming demos are included.",
            "Reminders tab: start with the 'Not updated' tile. These demos are past their time and still sit at Demo Fixed, so move each one to Demo Done or No Show.",
            "Open a lead to see the reminder times and the lead's note.",
            "Feedback tab: read low ratings first, then compare presenters.",
        ],
        whatYouGet: [
            "Reminders: demos booked, reminded, confirmed and moved, the attendance rate, and one row per booking with each reminder's status.",
            "Feedback: requests sent, links opened, answers, the average rating, ratings per presenter, and every answer.",
        ],
        terms: [
            ["Reminded", "At least one reminder for that booking went out by email or WhatsApp. Turn them on and set their times under Settings → Email Templates → Demo reminder."],
            ["Confirmed", "The lead tapped 'Yes, I'll join' on the reminder page."],
            ["Moved", "The lead picked a new time on the reminder page, or the demo was rebooked. The new booking gets its own row."],
            ["Attended", "The lead reached Demo Done or a later stage after the demo time."],
            ["Not updated", "The demo time has passed and the lead is still at Demo Fixed."],
        ],
        tips: [
            "Demos are counted by their booked time, so a demo moved into the period shows up there.",
            "The reminder dots are green when sent, red when failed and grey when not sent. Hover over a dot to see why.",
        ],
    },

    "/reports/nurture": {
        title: "Nurture",
        purpose: "Lists every lead parked in the Nurture stage: a real buyer who does not want it now, kept warm by email and WhatsApp until a rep revives them. Also shows what the nurture messages achieved in the period.",
        whenToUse: "Daily, for leads whose revisit date has come and leads that are warming up. Monthly, to see how many nurtured leads came back and were won.",
        howToUse: [
            "Call the leads at the top of the table first. They are either due for a revisit or have just clicked, replied or enquired again.",
            "Open a lead and press Revive on its Nurture card to move it back to Contacted. The normal status form opens, so write what changed.",
            "Pick a period (the last 90 days by default) for the tiles about stays, revivals and messages.",
            "If the open or click rates are low, change the step templates under Settings → Email Templates → Lead nurture.",
        ],
        whatYouGet: [
            "Tiles: leads in Nurture Lead now, revisits due, leads warming up, leads parked, revived and won in the period, and email and WhatsApp results.",
            "Counts by reason and by owner, and one row per nurtured lead with its revisit date, latest signal and last message.",
        ],
        terms: [
            ["Nurture Lead", "A lead stage for real leads who don't want to buy now. It needs a reason and a revisit date. Use Dis-Qualified for junk or wrong-fit leads."],
            ["Revisit due", "The revisit date chosen when the lead was parked is today or has passed. The owner was notified on the day."],
            ["Warming up", "The lead clicked a link in an email, replied on WhatsApp or enquired again while in Nurture Lead. The owner is notified."],
            ["Revived", "Moved from Nurture Lead back into the pipeline (for example to Contacted)."],
            ["Revived, since won", "A lead revived in the period that is now Closed Won or Onboarded."],
            ["Unsubscribed", "Leads whose nurture email was not sent because they used the unsubscribe link."],
        ],
        tips: [
            "Only the latest due step is sent, so a lead never gets several messages at once after a pause.",
            "Nurture leads are left out of the open pipeline, stale-lead alerts and AI suggestions, but their revisit date still appears in follow-ups.",
        ],
    },

    "/reports/conversion": {
        title: "Conversion Analytics",
        purpose: "How long each lead has been stuck in its current stage, how many times it has moved, and how old it is.",
        whenToUse: "To find deals stuck too long in one stage.",
        howToUse: [
            "Choose created date and rep, then a stage chip.",
            "Look for red 'Days in Stage' (30+ days) and low Transitions.",
            "Click a lead to open it.",
        ],
        whatYouGet: ["Per lead: Stage, Days in Stage, Transitions, Lead Age, Last Activity."],
        terms: [
            ["Days in Stage", "Days since the lead entered its current stage (from creation if unknown). Green under 14, amber 14–29, red 30+."],
            ["Transitions", "How many stage changes the lead has had."],
            ["Last Activity", "Date of the last stage change — not calls or messages."],
        ],
        tips: ["This is a per-lead table; it does not show overall conversion-rate charts."],
    },

    "/reports/email-analytics": {
        title: "Email Analytics",
        purpose: "Every email sent and received, and whether it was opened, clicked, replied to or bounced.",
        whenToUse: "To check whether customers are reading your emails and which templates get engagement.",
        howToUse: [
            "Set the period (Last 30 Days by default) and, optionally, the sending rep.",
            "Use All / Sent / Received and the Opened / Clicked / Bounced chips, or search by subject, customer or rep.",
            "Read the open and click rates in the footer.",
        ],
        whatYouGet: ["Per email: Date, Direction, Subject, To/From, Sent By, Template and engagement badges.", "Open rate and click rate."],
        terms: [["Open rate / Click rate", "Sent emails flagged opened / clicked ÷ all sent emails for the chosen period and rep."]],
        tips: ["Opens depend on the recipient's email app loading images, so real readership can be higher. No export."],
    },

    "/reports/audit-log": {
        title: "Audit Log",
        purpose: "A record of who did what in the system and when — creates, edits, deletes, logins, sends and settings changes.",
        whenToUse: "Investigating a changed or deleted record, a login problem or a settings change.",
        howToUse: [
            "Fill in both From and To dates — the date filter applies only when both are set.",
            "Narrow by type and action.",
            "Click 'show' in Details to see exactly what changed.",
        ],
        whatYouGet: ["Per event: Date / Time, User, Action, Type, Entity, IP, Details."],
        tips: ["Export CSV saves only the page currently on screen — increase rows per page first if you need more.", "Super Admins only."],
    },

    "/onboarded/reports/amc-tracker": {
        title: "AMC Tracker",
        purpose: "Which client AMCs (maintenance contracts) are overdue or expiring soon, and how much value is at stake.",
        whenToUse: "Weekly, to plan renewal calls and chase payments.",
        howToUse: [
            "Pick the AMC end-date window (Next 90 Days by default).",
            "Filter by status (Overdue / Expiring in 30d / later) and payment status.",
            "Click a client to open its AMC & Renewals tab. Export CSV saves the list.",
        ],
        whatYouGet: [
            "Summary: Total, Overdue, Expiring in 30d, Value at Risk.",
            "Per AMC: Client, Period, Value, Payment Terms, Days Left, Payment Status, Assigned To.",
        ],
        terms: [
            ["Days Left", "AMC end date minus today. Red 'overdue' when negative, amber within 30 days."],
            ["Value at Risk", "Total AMC value of every row listed."],
        ],
        tips: [
            "'Next N days' windows start today, so AMCs that are already overdue drop out — choose All Time to see them.",
            "The Assigned To filter currently has no effect.",
        ],
    },

    "/onboarded/reports/revenue": {
        title: "Onboarded Revenue",
        purpose: "Contract and AMC value of onboarded clients, and how many AMCs have been paid.",
        whenToUse: "Revenue reviews of the installed base.",
        howToUse: [
            "Choose the onboarded-date range and optionally a client status, then Apply.",
            "Click a client to open it.",
        ],
        whatYouGet: [
            "Summary: Total Clients, Contract Value, AMC Value, AMC Renewal Rate.",
            "Per client: Onboarded date, Status, Product, Payment Terms, Contract Value, AMC Value.",
        ],
        terms: [["AMC Renewal Rate", "AMC records marked Paid ÷ all AMC records for these clients — i.e. the share paid, not true renewals."]],
        tips: ["No export on this page — use the Onboarded Client List for Excel."],
    },

    "/onboarded/reports/client-list": {
        title: "Onboarded Client List",
        purpose: "All onboarded clients with value, contact person, location and remarks, and where details are missing.",
        whenToUse: "Sharing a client list, or cleaning up missing contacts and values.",
        howToUse: [
            "Filter by onboarded date, status, owner, and 'Has / Missing a contact'.",
            "Search by name, contact, phone or city.",
            "Click a client to open it; Excel downloads exactly what is filtered.",
        ],
        whatYouGet: [
            "Tiles: Clients, Total value, With a contact, With remarks — each saying how many are missing data.",
            "Per client: name, status, onboarded date, value, contact, phone, email, location, remarks.",
        ],
        tips: ["A dash means the data has not been entered for that client."],
    },

    "/onboarded/reports/implementation": {
        title: "Implementation Status",
        purpose: "How far each client's implementation has progressed, and which milestones are late.",
        whenToUse: "Implementation stand-ups and client escalations.",
        howToUse: [
            "Choose onboarded-date range and status.",
            "Use 'All Clients' for progress, 'Delayed Milestones' for what is overdue.",
            "Click a row to open the client. Export CSV saves the All Clients list.",
        ],
        whatYouGet: [
            "Summary: Total Clients, Completed, Delayed Milestones, Avg Completion.",
            "Per client: milestones done / total and progress %. Per delayed milestone: due date, days overdue, owner.",
        ],
        terms: [
            ["Progress", "Milestones marked Done ÷ all milestones."],
            ["Delayed", "A milestone not done and past its due date. Milestones on hold on the client's side are paused and not counted."],
            ["Completed", "Clients whose status is Completed (not necessarily 100% of milestones)."],
        ],
    },
};

// Shared wording for the Meta reports that only count leads carrying an ad id.
const NEEDS_AD_ID = "Only leads that carry a Meta ad id are counted. If the page says no lead carries an ad id, map campaign_id, adset_id and ad_id in the Zapier Lead Ads zap — attribution starts from the next lead, not past ones.";
const GST_NOTE = "All spend includes GST at the ad account's GST % (Settings → Meta Ads), so it is higher than Ads Manager, which shows spend before tax.";

const META_ADS = {
    "/reports/ad-performance": {
        title: "Ad Performance",
        purpose: "What each campaign, ad set and ad cost, and what its leads actually turned into in the CRM — demos, deals, revenue.",
        whenToUse: "The main report for deciding which campaigns and ads to scale, fix or switch off.",
        howToUse: [
            "Choose the period (Last 30 Days by default), ad account, objective and status (Active & paused / Active only / Paused only).",
            "Read the tiles, then the 'What is working, and why' panel — its tabs show Needs action and Working ads with what to do.",
            "Click a campaign to open its ad sets, and an ad set to open its ads.",
            "Search by campaign, ad set or ad name (press Enter). Excel downloads the table.",
        ],
        whatYouGet: [
            "Tiles: Spend, Impressions, Clicks (CTR · CPC), Meta Leads, Cost / Lead (and CRM CPL), Won / CAC.",
            "Per campaign / ad set / ad: Spend, Impressions, Clicks, CTR, CPC, Leads, CPL, CRM leads, funnel bar, CAC, ROAS.",
            "A verdict per ad: Working, Holding, Slipping, Not working or Too early, with findings and suggested action.",
        ],
        terms: [
            ["CPL", "Spend ÷ leads Meta reported."],
            ["CRM CPL", "Spend ÷ leads that actually reached the CRM."],
            ["CAC", "Spend ÷ deals won."],
            ["ROAS", "Revenue ÷ spend. Green at 1× or more (revenue covers the spend)."],
            ["Revenue", "Final quoted price of won leads created in the period."],
            ["Verdict", "Each ad is compared with the account's median CPL and CTR, Meta's rankings, frequency and how far its leads progressed. 'Too early' = not enough spend, impressions or leads to judge."],
        ],
        tips: [
            GST_NOTE,
            "A dash means the number cannot be worked out (e.g. no leads, so no CPL) — not zero.",
            "If an amber banner says many leads have no ad id, the CRM columns are understated.",
            "Empty table: run a sync from Settings → Meta Ads.",
        ],
    },

    "/reports/ad-dashboard": {
        title: "Live Ad Spend",
        purpose: "Today and month-to-date spend, leads and cost per lead for every running campaign, against your target CPL and daily budget.",
        whenToUse: "Several times a day while campaigns are running.",
        howToUse: [
            "Enter your target CPL in 'CPL target ₹' — until you do, every health dot stays grey.",
            "Scan the campaign cards: the dot colour shows whether CPL is on target, and the bar shows how much of the daily budget is used.",
            "Use 'Full report' to go to Ad Performance for detail.",
        ],
        whatYouGet: [
            "Tiles: Spend today (and MTD), Leads today, CPL today, CRM leads today, Demos MTD, Won MTD with CAC.",
            "Per campaign: health dot, today's spend vs yesterday, leads, CPL, daily budget bar and an MTD summary.",
        ],
        terms: [
            ["Health dot", "Green = CPL at or under target, amber = up to 25% over, red = more than 25% over. Uses today's CPL once a campaign has 5 leads today, otherwise the month-to-date CPL."],
            ["Budget bar", "Today's spend ÷ daily budget (both incl. GST). Amber from 70%, red from 95%."],
        ],
        tips: [GST_NOTE, "Figures are as of the last sync (shown at the top), so they lag Ads Manager slightly. Your target CPL should be GST-inclusive too."],
    },

    "/reports/ad-creative": {
        title: "Ad Creative",
        purpose: "Which creative — image, video, headline and text — is actually winning, with Meta's own quality rankings.",
        whenToUse: "Before producing new creatives, or to decide which ads to pause.",
        howToUse: [
            "Pick period, account and status. Sort by Spend, Leads, Cheapest CPL, Thumb-stop rate or Impressions.",
            "Set a minimum spend (e.g. ₹2,000+) so tiny-spend ads don't mislead you.",
            "Compare the cards; click the link icon to see the ad on Facebook.",
        ],
        whatYouGet: [
            "Tiles: Ads, Spend, Leads, Cost / Lead, Thumb-stop, and how many ads Meta grades above or below average.",
            "Per ad: thumbnail, copy, Spend / Leads / CPL / CTR, video hold (25/50/75/100% watched), and Quality, Engagement and Conversion rankings.",
        ],
        terms: [
            ["Thumb-stop rate", "Video plays ÷ impressions — how often the ad made people stop scrolling."],
            ["Video hold", "Share of viewers who reached 25%, 50%, 75% and 100% of the video."],
            ["Rankings", "Meta's latest grade versus competing ads. 'Bottom N%' is below average; 'Not enough data' means Meta could not rank it."],
        ],
        tips: [GST_NOTE, "Ads currently delivering always appear first."],
    },

    "/reports/ad-pacing": {
        title: "Spend Pacing",
        purpose: "Day-by-day spend against leads, flagging days where cost per lead jumped or money was spent with no leads.",
        whenToUse: "When results suddenly got worse and you need to find the day it started.",
        howToUse: [
            "Choose period, account and status.",
            "Pick how big a jump to flag (CPL +25%, +40%, +75% or +150%).",
            "Read the alerts list, then check those days in the chart and daily table.",
        ],
        whatYouGet: [
            "Tiles: Spend per day, Leads, Cost / Lead, Peak day, Days with no leads, Alerts.",
            "Chart of daily spend (bars) and leads (line); red bars are spend with zero leads.",
            "Daily table: Spend, Cumulative, Impressions, Clicks, Leads, CPL, CRM, Demos.",
        ],
        terms: [
            ["Spike day", "A day whose CPL is above the median of the previous 7 days by more than your chosen %. Needs at least 3 earlier days with leads."],
        ],
        tips: [GST_NOTE, "Bars and line use different scales — compare their trends, not their heights."],
    },

    "/reports/ad-audience": {
        title: "Ad Audience",
        purpose: "How your spend splits across age, gender, placement and device, and what a lead costs in each segment.",
        whenToUse: "To adjust targeting — cut segments with expensive leads, push budget to cheap ones.",
        howToUse: [
            "Choose the tab: Age & Gender, Placement or Device.",
            "Set period, account and status.",
            "Compare each segment's CPL with the blended CPL ('vs blended'); on Age & Gender the grid is green for cheap and red for dear.",
        ],
        whatYouGet: [
            "Tiles: Spend, Leads, Blended CPL, Cheapest segment.",
            "Per segment: Spend, Share, Impressions, Clicks, CTR, Leads, CPL, vs blended.",
        ],
        terms: [
            ["Blended CPL", "Total spend ÷ total leads across all segments."],
            ["vs blended", "How much dearer (+) or cheaper (−) the segment's CPL is than the blended CPL."],
            ["Share", "Segment spend ÷ total spend."],
        ],
        tips: [GST_NOTE, "Segment data is only synced for a recent window (see the note at the top), so older periods can look empty."],
    },

    "/reports/ad-geography": {
        title: "Ad Geography",
        purpose: "How your spend splits by region and where a lead is cheapest.",
        whenToUse: "To move budget towards regions with cheaper, better leads.",
        howToUse: [
            "Set period, account and status.",
            "Sort your attention by CPL and 'vs blended'.",
        ],
        whatYouGet: ["Tiles: Spend, Leads, Blended CPL, Cheapest region.", "Per region: Spend, Share, Impressions, Clicks, CTR, Leads, CPL, vs blended."],
        terms: [
            ["Blended CPL", "Total spend ÷ total leads across all regions."],
            ["vs blended", "How much dearer (+) or cheaper (−) the region's CPL is than the blended CPL."],
        ],
        tips: [GST_NOTE, "Region data is only synced for a recent window, so older periods can look empty."],
    },

    "/reports/ad-roi": {
        title: "Marketing ROI",
        purpose: "For each product/project: Meta spend beside closed revenue, with surplus or shortfall, ROAS and CAC.",
        whenToUse: "Monthly or quarterly marketing reviews and budget allocation between products.",
        howToUse: [
            "Choose the period (Last 90 Days by default).",
            "Leave 'Attributed leads only' off for blended ROI, or tick it to count only revenue from leads traced to a Meta ad.",
            "Compare projects in the Spend vs revenue chart and table.",
        ],
        whatYouGet: [
            "Tiles: Meta spend, Revenue, Surplus/Shortfall, ROAS, Deals won, CAC.",
            "Per project: Campaigns, Spend, Meta leads, CPL, CRM leads, Demos, Won, CAC, Revenue, ROAS.",
        ],
        terms: [
            ["Blended ROI", "Every won deal in the project, however the lead came in — not Meta-only."],
            ["Attributed ROI", "Only won deals from leads carrying a Meta ad id."],
        ],
        tips: [
            GST_NOTE,
            "Using the status filter without 'Attributed leads only' overstates ROAS — the page warns you.",
            "Campaigns under 'Unassigned' need mapping in Settings → Meta Ads.",
        ],
    },

    "/reports/ad-attribution": {
        title: "Lead Attribution",
        purpose: "Traces each lead to the exact campaign, ad set and ad that produced it, and shows how far those leads got.",
        whenToUse: "To find the ads that bring deals, not just leads.",
        howToUse: [
            "Choose period (Last 90 Days by default), account and status.",
            "Expand a campaign → ad set → ad; compare Win % and CPL.",
            "Click the ad link to view it on Facebook.",
        ],
        whatYouGet: [
            "Tiles: Attributed leads, Quality, Demos, Won (win rate), Spend per lead, CAC and revenue.",
            "Per campaign / ad set / ad: Leads, Quality, Demos, Won, Win %, lead-to-won bars, Spend, CPL, Revenue.",
        ],
        terms: [
            ["Win %", "Won ÷ attributed leads. Green at 10% or more."],
            ["CPL (here)", "Spend ÷ leads in the CRM traced to the ad (not Meta's lead count)."],
        ],
        tips: [NEEDS_AD_ID, GST_NOTE],
    },

    "/reports/ad-funnel": {
        title: "Funnel by Ad",
        purpose: "For each ad or campaign, where its leads stop moving: contacted, demo fixed, demo done, proposal, won.",
        whenToUse: "When an ad brings leads but few deals — to see which step is leaking.",
        howToUse: [
            "Choose period, account, status and Per ad / Per campaign.",
            "Check the Overall funnel, then compare each card's drop-offs.",
        ],
        whatYouGet: [
            "Tiles: Attributed leads, Reached a demo, Won, Biggest drop-off.",
            "A funnel per ad/campaign with win rate, % reaching a demo and spend per win.",
        ],
        terms: [
            ["Drop (−%)", "Share lost since the previous step. Amber at 30%+, red at 50%+."],
            ["Reached a demo", "Leads that got to Demo Fixed ÷ all leads."],
        ],
        tips: [NEEDS_AD_ID],
    },

    "/reports/ad-lead-quality": {
        title: "Lead Quality by Ad",
        purpose: "Which ads bring good leads, not just many — demo rate, disqualified rate, and how quickly leads get contacted.",
        whenToUse: "When sales says 'the leads are junk' — to find out which ads are responsible.",
        howToUse: ["Choose period, account and status.", "Compare ads by Score, then by Disqualified and Never contacted."],
        whatYouGet: [
            "Tiles: Attributed leads, Average quality, Time to first contact, Best ad.",
            "Per ad: Score, Leads, CPL, Reached demo, Disqualified, Never contacted, Avg unanswered, Time to 1st contact, AI score, Hot/Med/Cold mix.",
        ],
        terms: [
            ["Score", "0–100: 55% demo rate + 25% not-disqualified + 20% contacted. Green 70+, amber 45+, red below."],
            ["To 1st contact", "Average time from lead creation to its first stage change."],
            ["Hot/Med/Cold", "From the Lead Potential set on each lead."],
        ],
        tips: [NEEDS_AD_ID, "Live ads are listed first, so 'Best ad' is the best live ad."],
    },

    "/reports/ad-rep-effectiveness": {
        title: "Rep × Ad Effectiveness",
        purpose: "Close rate of each rep on leads from each campaign or ad — shows where one rep converts leads another cannot.",
        whenToUse: "Deciding which rep should get leads from which campaign.",
        howToUse: [
            "Choose period, account, status, By campaign / By ad, and a minimum volume (3+ leads by default).",
            "Read across a rep's row; hover a cell for leads, demos and won.",
        ],
        whatYouGet: ["Tiles: Reps, Attributed leads, Won, Overall close rate.", "A grid of close % per rep per campaign/ad."],
        terms: [
            ["Close rate", "Won ÷ leads."],
            ["Cell colour", "Dark green = 1.5× the overall rate or better, light green 1.1×+, amber 0.5–0.9×, red under half."],
        ],
        tips: [NEEDS_AD_ID, "Leads also need an assigned rep. Small sources are hidden by the volume filter; up to 20 columns show."],
    },

    "/reports/ad-time-lag": {
        title: "Time to Revenue",
        purpose: "How many days each ad's leads take to reach a demo and to close — compares ads on speed, not just volume.",
        whenToUse: "Cash-flow planning, and choosing between ads with similar cost per lead.",
        howToUse: ["Choose period, account, status and Per ad / Per campaign.", "Compare median days to demo and to won; then read the monthly cohorts."],
        whatYouGet: [
            "Tiles: Attributed leads, Median lead → demo, Median lead → won, Deals closed.",
            "Per ad: timeline bar, days to demo and won, fastest, slowest, CPL, spend per day to revenue.",
            "Cohorts by lead month: leads, demos, demo rate, won, win rate, median days to won.",
        ],
        terms: [
            ["Median days", "Typical days from lead creation to first demo / won (the middle value, so one slow deal does not skew it)."],
            ["Spend / day to revenue", "Spend ÷ median days to won."],
        ],
        tips: [NEEDS_AD_ID, "Recent months always look worse — their leads have not had time to close yet."],
    },

    "/reports/ad-conversions": {
        title: "Meta Conversions",
        purpose: "CRM outcomes (qualified, demo, won…) waiting to be sent back to Meta, so Meta can optimise ads toward real buyers instead of form-fillers.",
        whenToUse: "Regularly — the sooner outcomes are sent, the better Meta learns. Meta refuses outcomes older than 7 days.",
        howToUse: [
            "Check the pixel banner — test mode or live, and when it last sent.",
            "Review the Waiting tab: which outcome becomes which Meta event, and whether each will send.",
            "Click 'Send N to Meta'. Check the Sent tab afterwards.",
        ],
        whatYouGet: [
            "Tiles: Ready to send, Skipped, Deal value, Matched by lead id / by phone.",
            "Waiting and Sent lists with event, match method, value and status.",
        ],
        terms: [
            ["Matched on", "How Meta will recognise the person: Meta's lead id, or phone/email (hashed before sending)."],
            ["Skipped", "Too old (over 7 days) or no way to identify the person."],
        ],
        tips: [
            "Needs a pixel connected and stage-to-event mappings in Settings → Meta Ads.",
            "Each outcome is sent only once. Test mode events appear in Events Manager but are not used for optimisation.",
        ],
    },
};

const ENGAGE = {
    "/engage/reports": {
        title: "Enquiry Reports Dashboard",
        purpose: "How many enquiries came in, how many converted, and how much revenue is realised or still in the pipeline — sliced any way you need.",
        whenToUse: "Management reviews, and to answer questions like 'how did cardiology enquiries from corporates do last quarter?'",
        howToUse: [
            "Pick the period (Last 90 days by default).",
            "Use '+ Add filter' for Patient type, Stage, Source, Department, Assigned to, Referring doctor or Corporate account. Stage needs a Patient type first.",
            "Choose 'Group by' (Patient type, Stage, Source, Department, Assigned to or Status) to change the rows of the table.",
        ],
        whatYouGet: [
            "Summary line: enquiries, converted, conversion %, revenue realised.",
            "Per group: Enquiries, Active, Converted, Lost, Conv. rate, Realised ₹, Pipeline ₹, with a total row.",
        ],
        terms: [
            ["Realised ₹", "Value of converted enquiries."],
            ["Pipeline ₹", "Full value of still-active enquiries (not reduced by their chance of converting)."],
            ["Enquiry value", "Its quoted value; if none, the procedure catalog price; if none, the department's average revenue per patient; otherwise ₹15,000. A footnote warns when estimates are included."],
        ],
        tips: ["Dates refer to when the enquiry was created."],
    },

    "/engage/reports/enquiry-funnel": {
        title: "Enquiry Funnel",
        purpose: "How enquiries move through the stages, and which sources convert best.",
        whenToUse: "To see where patients drop out and which channels bring patients who actually convert.",
        howToUse: ["Choose the period ('All time' shows the last 90 days).", "Read the stage funnel, then compare sources in the table."],
        whatYouGet: [
            "Cards: Total enquiries, Converted, Overall conversion.",
            "Stage funnel with counts, % of total and the drop from the previous stage.",
            "By source: Total, Converted, Conv. %, Dropped.",
        ],
        terms: [["Conv. %", "Converted ÷ total. Green 30%+, amber 15%+, red below."]],
    },

    "/engage/reports/source-roi": {
        title: "Source ROI",
        purpose: "For each channel: enquiries, conversions and what each converted patient cost.",
        whenToUse: "Deciding where to spend on patient acquisition.",
        howToUse: ["Choose the period.", "Compare Conv. % and Cost / patient across sources."],
        whatYouGet: ["Per source: Total, Converted, Dropped, Conv. %, Spend, Cost / patient."],
        terms: [
            ["Spend", "Only for paid channels: health camps (actual spend, else budget) and digital marketing campaigns."],
            ["Cost / patient", "Spend ÷ converted patients."],
        ],
        tips: ["Spend is all-time even when you narrow the dates, so cost per patient looks high for short periods."],
    },

    "/engage/reports/referrer-leaderboard": {
        title: "Referrer Leaderboard",
        purpose: "Which referring doctors send the most patients, how many convert, and the incentives paid or still owed.",
        whenToUse: "Doctor-relations reviews and incentive payouts.",
        howToUse: ["Choose the period ('All time' shows the last 90 days).", "Rank by conversions, referrals or payouts.", "Click a doctor to open their profile."],
        whatYouGet: ["Top 25 doctors: Referrals, Converted, Conv. %, Paid, Owed."],
        terms: [
            ["Conv. %", "Converted ÷ referrals. Green 40%+, amber 20%+."],
            ["Paid / Owed", "Incentive amounts marked paid / not yet paid."],
        ],
        tips: ["Only doctors with at least one referral in the period appear."],
    },

    "/engage/reports/camp-roi": {
        title: "Health Camp ROI",
        purpose: "Whether each health camp paid off — attendance, enquiries, conversions and cost per converted patient.",
        whenToUse: "After a camp, and when planning the next one.",
        howToUse: ["Choose the period (by camp start date).", "Compare camps on Converted and Cost / patient; click a camp to open it."],
        whatYouGet: [
            "Cards: Attended, Leads generated, Converted (%), Cost / patient.",
            "Per camp: Date, Attended, Leads, Converted, Spend, Cost / patient.",
        ],
        terms: [
            ["Attended", "Actual footfall recorded on the camp."],
            ["Leads", "Enquiries with source Health Camp linked to that camp."],
            ["Spend", "Actual spend, or the budget if actual spend was not entered."],
        ],
        tips: ["Record actual footfall and spend when completing a camp, or the numbers fall back to the budget."],
    },

    "/engage/reports/marketing-roi": {
        title: "Digital Marketing ROI",
        purpose: "What each digital campaign cost per enquiry and per converted patient.",
        whenToUse: "Comparing campaigns and platforms.",
        howToUse: ["Choose the period (by campaign start date) and platform.", "Compare CPL, CPA and Conv. %; click a campaign to open it."],
        whatYouGet: ["Cards: Total spend, Total leads, Avg CPL, Avg CPA.", "Per campaign: Platform, Spend, Leads, Converted, CPL, CPA, Conv. %."],
        terms: [
            ["CPL", "Spend ÷ enquiries linked to the campaign."],
            ["CPA", "Spend ÷ converted patients."],
        ],
        tips: ["Spend is what was entered on the campaign. Campaigns with no start date disappear when a date range is set."],
    },

    "/engage/reports/meta-performance": {
        title: "Meta Performance",
        purpose: "How many patient enquiries each Facebook / Instagram campaign produced, what happened to them, and the cost per enquiry and per conversion.",
        whenToUse: "Reviewing Meta campaigns with your agency or marketing team.",
        howToUse: [
            "Set From / To dates and the platform (Meta, Facebook only, Instagram only).",
            "Compare campaigns in the table, then use the outcome chips (Converted, In progress, Not yet worked, Lost) and search to read the enquiries.",
        ],
        whatYouGet: [
            "Cards: Enquiries, Converted, Spend, Cost / enquiry, Cost / conversion, Expected value.",
            "Per campaign: Enquiries, Converted, Lost, Conv %, Spend, CPL, CPA, Expected value.",
            "Enquiry list with stage, outcome, owner and expected value.",
        ],
        terms: [
            ["Not yet worked", "Enquiry still in status New."],
            ["Expected value", "Treatment value recorded on the enquiries — an estimate, not revenue received. 'N× spend' = expected value ÷ spend."],
            ["Spend", "Spend synced from Meta (incl. GST) where available, otherwise the spend entered on the campaign."],
        ],
        tips: [
            "Digital enquiries with no campaign are shown as 'Unattributed' and are left out of cost figures.",
            "The enquiry list shows the first 500 rows.",
        ],
    },

    "/engage/reports/no-show-rate": {
        title: "No-Show Rate",
        purpose: "Of the patients who booked appointments, how many actually came.",
        whenToUse: "To improve reminders and confirmation calls, by source.",
        howToUse: ["Choose the period and optionally a source.", "Compare show rate by source."],
        whatYouGet: ["Cards: Booked, Attended, No-show, Show rate.", "The same per source."],
        terms: [
            ["Attended", "Enquiry has reached Consulted, Under treatment, Follow-up required or Converted."],
            ["Show rate", "Attended ÷ booked. Green 70%+, amber 40%+."],
        ],
        tips: ["Upcoming appointments and dropped/lost enquiries count as no-shows, so the no-show figure runs high."],
    },

    "/engage/reports/followup-completion": {
        title: "Follow-up Completion",
        purpose: "Whether staff complete their scheduled follow-ups on time.",
        whenToUse: "Weekly team reviews.",
        howToUse: ["Choose the period (by scheduled date; 'All time' shows the last 90 days).", "Look for staff with low Completion % and high Overdue."],
        whatYouGet: ["Cards: Total, Completed, Scheduled, Overdue, Completion %.", "Per staff member: Assigned, Completed, Overdue, Completion %."],
        terms: [
            ["Overdue", "Still scheduled and past its scheduled time."],
            ["Completion %", "Completed ÷ total. Green 80%+, amber 50%+."],
        ],
    },

    "/engage/reports/engagement": {
        title: "Engagement",
        purpose: "Grades recent open enquiries Hot, Warm or Cold by how recently and how much they have been contacted, and flags ones needing attention.",
        whenToUse: "Daily, to pick which patients to call next.",
        howToUse: ["Optionally choose a period and stage.", "Work the Cold list and anything with a red alert icon (hover to read it). Click a patient to open the enquiry."],
        whatYouGet: ["Counts of Hot, Warm and Cold, and up to 10 patients per grade with score and alerts."],
        terms: [
            ["Score", "Recent contact weighted by type (call 10, WhatsApp 6, email 5, SMS 4, document 4, note 3…), fading over about two weeks. Hot 30+, Warm 10+, Cold below 10."],
            ["Alerts", "No contact yet, last contact 7+ days ago, stuck in a stage 5+ days, or follow-up overdue."],
        ],
        tips: ["Only the 30 newest open enquiries are scored — this is a working list, not a hospital-wide count."],
    },

    "/engage/reports/conversion": {
        title: "Conversion Analytics",
        purpose: "At which step — contacted, interested, appointment booked, consulted, converted — enquiries are lost.",
        whenToUse: "To find the step of the patient journey that needs fixing.",
        howToUse: ["Choose the period and optionally a source.", "Look for the biggest 'Dropped' between two steps."],
        whatYouGet: ["A funnel from New to Converted, with count, % of top and drop from the previous step."],
        terms: [["Step count", "Enquiries currently at or beyond that step."]],
        tips: ["Dropped and lost enquiries are left out, so real losses are larger than shown."],
    },

    "/engage/reports/timeline": {
        title: "Timeline",
        purpose: "A feed of recent activity on enquiries — calls, notes, WhatsApp, emails, status changes.",
        whenToUse: "To check what was done on the latest enquiries.",
        howToUse: ["Choose the period (Last 30 days by default) and activity type.", "Click an enquiry name to open it."],
        whatYouGet: ["Newest-first activity cards: enquiry, date and time, type, message."],
        tips: ["Only covers the 20 most recently created enquiries — it is not a full audit trail."],
    },

    "/engage/reports/corporate-renewals": {
        title: "Corporate Renewals",
        purpose: "Which corporate contracts have expired or are due for renewal soon.",
        whenToUse: "Monthly, to start renewal talks in time.",
        howToUse: ["Choose a bucket (Expired, Critical, Renew soon, Comfortable).", "Click a company to open its account."],
        whatYouGet: ["Counts per bucket, and per company: contract end, days left, discount %."],
        terms: [["Buckets", "Expired = end date passed; Critical = within 30 days; Renew soon = 31–90 days; Comfortable = over 90 days."]],
        tips: ["Terminated accounts and accounts with no end date are not shown."],
    },

    "/engage/reports/revenue-source/:source": {
        title: "Revenue Deep Dive",
        purpose: "For one lead source, how its realised, pipeline and projected revenue — and ROI — are built, enquiry by enquiry.",
        whenToUse: "When a revenue figure on the dashboard needs explaining.",
        howToUse: [
            "Open it by clicking a source in the dashboard's revenue panel — the period comes from there.",
            "Switch between 'Pipeline' and 'Realised (period)'. Click any row to open the enquiry.",
            "Read 'How this is calculated' at the bottom.",
        ],
        whatYouGet: [
            "Cards: Historical enquiries, Ever converted, Historical conv-rate, Realised, Pipeline (expected), Projected total with spend and ROI.",
            "Per enquiry: status, department, procedure, value used, value tier, expected value, owner.",
        ],
        terms: [
            ["Value tier", "Quoted (its own value), Catalog (procedure price) or Fallback (department average, else ₹15,000)."],
            ["Pipeline (expected)", "Active enquiries' value × this source's historical conversion rate (20% if it has no history)."],
            ["ROI multiple", "Projected total ÷ spend."],
        ],
        tips: ["Spend is all-time. For doctor and ambulance referrals it means unpaid incentives."],
    },
};

const REPORT_GUIDES = { ...SALES, ...SALES_MORE, ...META_ADS, ...ENGAGE };

/** Guide for an exact route, e.g. a report card's `route`. */
export const getReportGuide = (route) => REPORT_GUIDES[route] || null;

/**
 * Guide for the page at `pathname`. Exact match first, then routes with a
 * `:param` segment (e.g. /engage/reports/revenue-source/:source).
 */
export const findReportGuide = (pathname) => {
    const path = (pathname || "").replace(/\/+$/, "") || "/";
    if (REPORT_GUIDES[path]) return REPORT_GUIDES[path];
    const parts = path.split("/");
    const key = Object.keys(REPORT_GUIDES).find((route) => {
        if (!route.includes(":")) return false;
        const pattern = route.split("/");
        return pattern.length === parts.length
            && pattern.every((seg, i) => seg.startsWith(":") || seg === parts[i]);
    });
    return key ? REPORT_GUIDES[key] : null;
};

export default REPORT_GUIDES;
